import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Component, useEffect, lazy, Suspense } from 'react';
import { NotificationsProvider } from './context/NotificationsContext';
import NotificationToast from './components/NotificationToast';
import TermsGate from './components/TermsGate';
import { useAuth } from './context/AuthContext';
import { DialogHost } from './dialog';
import { useEdgeSwipe } from './swipeBack';
// Login is the entry screen (path "/"), so it's imported eagerly — it must be
// in the first chunk to paint immediately. Every other page is code-split with
// React.lazy so the initial download/parse the login screen waits on stays
// small; each route's JS is only fetched when the user navigates to it.
import Login from './pages/Login';
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const EmailConfirmed = lazy(() => import('./pages/EmailConfirmed'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const CreateProfile = lazy(() => import('./pages/CreateProfile'));
const SearchSetup = lazy(() => import('./pages/SearchSetup'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const Swipe = lazy(() => import('./pages/Swipe'));
const Watchlist = lazy(() => import('./pages/Watchlist'));
const RoleSelect = lazy(() => import('./pages/RoleSelect'));
const CreateListing = lazy(() => import('./pages/CreateListing'));
const HostDashboard = lazy(() => import('./pages/HostDashboard'));
const Matches = lazy(() => import('./pages/Matches'));
const Chat = lazy(() => import('./pages/Chat'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ViewProfile = lazy(() => import('./pages/ViewProfile'));
const Notifications = lazy(() => import('./pages/Notifications'));
const NotificationAlerts = lazy(() => import('./pages/NotificationAlerts'));
const Blast = lazy(() => import('./pages/Blast'));
const About = lazy(() => import('./pages/About'));
const Terms = lazy(() => import('./pages/Terms'));
const BetaFeedback = lazy(() => import('./pages/BetaFeedback'));

// Where an authenticated profile belongs. A known role maps to its own home;
// anything else (null, legacy, mid-switch) goes to /role-select to be resolved.
// The redirect target ALWAYS matches the profile's role, so the destination
// guard accepts it — this is what makes the guards loop-proof. The old code
// bounced mismatches to the *other* protected route, so an unknown role would
// ping-pong /swipe <-> /host-dashboard forever and freeze the WebView.
function homeForProfile(profile) {
  if (profile?.role === 'host') return '/host-dashboard';
  if (profile?.role === 'subleasee') return '/swipe';
  return '/role-select';
}

// useEffect (not useLayoutEffect) so a redirect can never pin the main thread
// before the browser gets a chance to paint.
function HostRoute({ children }) {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const ok = !loading && profile && profile.role === 'host';
  useEffect(() => {
    // Signed out → the sign-in screen (not /role-select, which needs an account).
    if (!loading && !ok) navigate(user ? homeForProfile(profile) : '/', { replace: true });
  }, [loading, ok, user, profile, navigate]);
  if (!ok) return null;
  return children;
}

function AuthedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const ok = !loading && !!user;
  useEffect(() => {
    if (!loading && !ok) navigate('/', { replace: true });
  }, [loading, ok, navigate]);
  if (!ok) return null;
  return children;
}

function SubleaseRoute({ children }) {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const ok = !loading && profile && profile.role === 'subleasee';
  useEffect(() => {
    if (!loading && !ok) navigate(user ? homeForProfile(profile) : '/', { replace: true });
  }, [loading, ok, user, profile, navigate]);
  if (!ok) return null;
  return children;
}

// The browse feed: open to signed-out guests (App Store Guideline 5.1.1(v) —
// browsing can't require an account), otherwise nomads only, like SubleaseRoute.
// A signed-in user who isn't a nomad yet still gets routed to their own home.
function BrowseRoute({ children }) {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const ok = !loading && (!user || (profile && profile.role === 'subleasee'));
  useEffect(() => {
    if (!loading && !ok) navigate(homeForProfile(profile), { replace: true });
  }, [loading, ok, profile, navigate]);
  if (!ok) return null;
  return children;
}

// Shown while a lazily-loaded route chunk is being fetched. Kept intentionally
// minimal (just the app background) so there's no layout flash between chunks.
function RouteFallback() {
  return <div style={{ minHeight: '100%', backgroundColor: 'var(--bg-page)' }} />;
}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f7f7f7', fontFamily: 'inherit' }}>
          <div style={{ backgroundColor: 'var(--bg-card)', padding: '40px', borderRadius: '16px', boxShadow: 'var(--shadow-detail-card)', maxWidth: '480px', width: '90%' }}>
            <h2 style={{ margin: '0 0 12px', color: 'var(--text-primary)' }}>Something went wrong</h2>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '14px' }}>The app ran into an error. Try refreshing the page.</p>
            <pre style={{ background: 'var(--bg-card-alt)', padding: '12px', borderRadius: '8px', fontSize: '12px', color: 'var(--danger)', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {this.state.error.message}
            </pre>
            <button onClick={() => window.location.reload()} style={{ marginTop: '16px', padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: 'var(--btn-solid-bg)', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Routed pages fade in on navigation instead of hard-cutting. The wrapper is
// keyed on the pathname so it remounts (replaying `nm-fade`) each time the route
// changes; height:100% keeps the iOS fixed-height chain intact (#root is
// position:fixed inset:0, so 100% here is a definite length — see index.css).
function FadeSuspense({ children }) {
  const location = useLocation();
  return (
    <Suspense fallback={<RouteFallback />}>
      <div key={location.pathname} className="nm-fade" style={{ height: '100%' }}>
        {children}
      </div>
    </Suspense>
  );
}

// Left-edge swipe: opens the menu on main screens, or goes to a sub-screen's
// home screen (see swipeBack.js). Lives inside the Router; renders nothing.
function SwipeBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  useEdgeSwipe(navigate, location, {
    userId: user?.id,
    role: profile?.role,
    homePath: user ? homeForProfile(profile) : '/swipe',
  });
  return null;
}

function App() {
  // Warm the main tab chunks after first idle so switching tabs doesn't stall on
  // a lazy-chunk download (the blank-then-snap on first visit to a route).
  useEffect(() => {
    const prefetch = () => {
      import('./pages/Swipe');
      import('./pages/Watchlist');
      import('./pages/Matches');
      import('./pages/HostDashboard');
      import('./pages/Notifications');
    };
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = ric(prefetch);
    return () => cancel(id);
  }, []);

  return (
    <ErrorBoundary>
    <Router>
      <NotificationsProvider>
        <NotificationToast />
        <DialogHost />
        <SwipeBack />
        <TermsGate>
        <FadeSuspense>
        <Routes>
          <Route path="/" element={<Login />} />
          {/* Public pages — no auth. /about is the business-verification URL
              given to Stripe and other partners. */}
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/email-confirmed" element={<EmailConfirmed />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/role-select" element={<RoleSelect />} />
          <Route path="/create-profile" element={<CreateProfile />} />
          <Route path="/setup-search" element={<SubleaseRoute><SearchSetup /></SubleaseRoute>} />
          <Route path="/edit-profile" element={<EditProfile />} />
          <Route path="/account-settings" element={<AccountSettings />} />
          <Route path="/swipe" element={<BrowseRoute><Swipe /></BrowseRoute>} />
          <Route path="/watchlist" element={<SubleaseRoute><Watchlist /></SubleaseRoute>} />
          <Route path="/matches" element={<SubleaseRoute><Matches /></SubleaseRoute>} />
          <Route path="/alerts" element={<SubleaseRoute><NotificationAlerts /></SubleaseRoute>} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/beta-feedback" element={<BetaFeedback />} />
          <Route path="/create-listing" element={<HostRoute><CreateListing /></HostRoute>} />
          <Route path="/host-dashboard" element={<HostRoute><HostDashboard /></HostRoute>} />
          <Route path="/blast" element={<HostRoute><Blast /></HostRoute>} />
          <Route path="/chat/:matchId" element={<Chat />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/profile/:userId" element={<AuthedRoute><ViewProfile /></AuthedRoute>} />
        </Routes>
        </FadeSuspense>
        </TermsGate>
      </NotificationsProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;